import { loginSchema } from '@/features/auth/schemas/login.schema.ts';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import { EyePasswordShow } from '@/assets/auth/eye-password-show.tsx';
import { EyePasswordHide } from '@/assets/auth/eye-password-hide.tsx';
import { Button } from '@/components/ui/button.tsx';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useLoginUserQuery } from '@/features/auth/hooks/mutations/login.ts';
import { Link, useNavigate } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert.tsx';
import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import Logo from '@tenant/logo';
import { useAuthStore } from '@/services/store/use-auth-store.tsx';

const credentialsSchema = loginSchema.pick({
  email: true,
  password: true,
});

type Credentials = z.infer<typeof credentialsSchema>;

export function LoginCredentialsForm() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const { setTokenExpiration, setUserRole } = useAuthStore();

  const form = useForm({
    resolver: zodResolver(credentialsSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const {
    mutate: login,
    isPending,
    error,
  } = useLoginUserQuery({
    onSuccess: (data) => {
      if ('redirectTo' in data) {
        if (data.redirectTo === 'TOTP_VERIFICATION') {
          navigate('verification-2fa', {
            replace: true,
            state: {
              from: 'LOGIN_PAGE',
            },
          });
          return;
        }

        return navigate('/', { replace: true });
      }

      const { refreshToken, role } = data;
      if (refreshToken && refreshToken?.expiresAt && role) {
        setTokenExpiration(refreshToken.expiresAt);
        setUserRole(role);

        navigate('/setup-2fa', {
          replace: true,
          state: {
            from: 'LOGIN_PAGE',
          },
        });
      }
    },
  });

  const handleSubmit = (credentials: Credentials) => {
    login(credentials);
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev);
  };

  return (
    <div>
      <div className="flex flex-col items-center justify-center gap-2">
        <Logo width="200" height="58" />
      </div>
      <div className="mt-8">
        <div className="flex max-w-[400px] flex-col gap-4">
          {!!error?.message && (
            <Alert variant="destructive" className="flex items-center gap-4">
              <div className="h-4 w-4">
                <ExclamationTriangleIcon className="h-4 w-4" />
              </div>
              <AlertDescription>{error?.message}</AlertDescription>
            </Alert>
          )}
          <h2 className="text-2xl font-bold">{t('auth.login.title')}</h2>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-md font-medium">
                      {t('auth.login.email')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        autoComplete="username"
                        placeholder={t('auth.login.emailPlaceholder')}
                        className="text-md"
                      />
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-md font-medium">
                      {t('auth.login.password')}
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          type={isPasswordVisible ? 'text' : 'password'}
                          autoComplete="current-password"
                          placeholder={t('auth.login.passwordPlaceholder')}
                          className="text-md pr-12"
                        />
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className="absolute top-1 right-4 rounded-lg p-1 transition-colors hover:bg-[#e2e8f0]"
                          tabIndex={-1}
                          aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                        >
                          {isPasswordVisible ? (
                            <EyePasswordShow width="19" height="19" />
                          ) : (
                            <EyePasswordHide width="19" height="19" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={isPending}
                className="text-md flex w-full items-center"
                size="lg"
              >
                {isPending ? (
                  <>
                    <LoadingSpinner className="mr-2 h-4 w-4" />
                    <span className="text-[16px] select-none">
                      {t('auth.login.loggingIn')}
                    </span>
                  </>
                ) : (
                  <span className="text-[16px] select-none">
                    {t('auth.login.loginButton')}
                  </span>
                )}
              </Button>
            </form>
          </Form>
          <div>
            <div className="flex justify-center gap-1 text-center">
              <span>{t('auth.login.noAccount')}</span>
              <Link to="/signup" className="link-underline">
                {t('auth.login.requestAccount')}
              </Link>
            </div>
            <div className="flex justify-center gap-1 text-center">
              <Link to="/forgot-password" className="link-underline">
                {t('auth.login.forgotPassword')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
